import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/ui';
import { formatMoney } from '../../lib/format';
import { links } from '../../lib/links';
import { useSession } from '../../lib/session';
import { KpiStrip, type Kpi } from './primitives';
import { QuickActions, StickyQuickActions, type QuickAction } from './QuickActions';
import { dashboardDomains } from './presets';
import { BatchBoardWidget } from './widgets/BatchBoardWidget';
import { ExpiringWidget } from './widgets/ExpiringWidget';
import { HoldQueueWidget } from './widgets/HoldQueueWidget';
import { LedgerIntegrityWidget } from './widgets/LedgerIntegrityWidget';
import { LowStockWidget } from './widgets/LowStockWidget';
import { RecentAuditWidget } from './widgets/RecentAuditWidget';
import { RecentMovementsWidget } from './widgets/RecentMovementsWidget';
import { RecentRecoveriesWidget } from './widgets/RecentRecoveriesWidget';
import { lowStockQuery, onHoldQuery, openBatchesQuery, stockValuationQuery } from './queries';

const OPEN_STATUSES = new Set(['draft', 'in_progress', 'completed']);

/**
 * A custom role's dashboard.
 *
 * Custom roles are defined by picks from the capability catalogue, so the screen
 * is composed from whichever domains those capabilities belong to, plus the two
 * reads that are useful to everyone. It never guesses from the role's name.
 */
export function CustomDashboard() {
  const { capabilities, can, shows } = useSession();
  const domains = dashboardDomains(capabilities);

  const warehouse = domains.includes('warehouse');
  const production = domains.includes('production');
  const quality = domains.includes('quality');
  const admin = domains.includes('admin');

  const valuation = useQuery(stockValuationQuery);
  const lowStock = useQuery(lowStockQuery);
  const hold = useQuery(onHoldQuery);
  const board = useQuery(openBatchesQuery);

  const lowCount = lowStock.data?.items.length;
  const holdCount = hold.data?.items.length;
  const openBatches = (board.data?.items ?? []).filter((batch) => OPEN_STATUSES.has(batch.status)).length;

  const kpis: Kpi[] = [
    {
      label: 'Stock value',
      value: valuation.data ? formatMoney(valuation.data.totals.quantityValue) : '—',
      to: shows('reports') ? '/reports' : undefined,
    },
    {
      label: 'Low stock',
      value: lowCount === undefined ? '—' : String(lowCount),
      tone: lowCount ? 'warn' : 'good',
    },
  ];
  if (production || admin) {
    kpis.push({ label: 'Open batches', value: board.isSuccess ? String(openBatches) : '—' });
  }
  if (quality || admin) {
    kpis.push({ label: 'On hold', value: holdCount === undefined ? '—' : String(holdCount) });
  }

  const actions: QuickAction[] = [];
  if (warehouse) {
    actions.push({ label: 'Receive', to: links.inventoryReceive(), capability: 'inventory.manage', tone: 'primary' });
    actions.push({ label: 'Issue', to: links.inventoryAction('issue'), capability: 'inventory.manage' });
    actions.push({ label: 'Transfer', to: links.inventoryAction('transfer'), capability: 'inventory.manage' });
  }
  if (production) {
    actions.push({ label: 'New batch', to: links.newBatch(), capability: 'batch.manage' });
    actions.push({ label: 'Record recovery', to: links.newRecovery(), capability: 'recovery.record' });
  }

  return (
    <>
      <PageHeader
        eyebrow="Dashboard"
        title="Your workspace"
        description="Built from what your role is allowed to do."
      />

      <KpiStrip items={kpis} />

      {actions.length > 0 ? (
        <div className="mt-4">
          <QuickActions actions={actions} />
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <LowStockWidget to={shows('inventory') ? links.inventoryBalances() : undefined} />
        {warehouse ? <ExpiringWidget to={shows('inventory') ? links.inventoryBalances() : undefined} /> : null}
        {production ? <BatchBoardWidget to={shows('batches') ? '/batches' : undefined} /> : null}
        {quality ? <HoldQueueWidget canDecide={can('qc.release')} /> : null}
        {production ? <RecentRecoveriesWidget to={shows('recovery') ? '/recovery' : undefined} /> : null}
      </div>

      {admin ? (
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <LedgerIntegrityWidget />
          {can('audit.read') ? <RecentAuditWidget to="/audit" /> : null}
        </div>
      ) : null}

      <div className="mt-4">
        <RecentMovementsWidget to={shows('inventory') ? links.inventoryLedger() : undefined} />
      </div>

      <StickyQuickActions actions={actions} />
    </>
  );
}
